"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import {
  extractProperty,
  extractPropertyMediaItems,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
} from "utils"
import PortTitle from "./PortTitle"

const PORT_BLUE_IMAGE_URL = "/images/port-blue-static.png"

const StyledSection = styled.section<{ backgroundColor?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 40px;
  background-color: ${(props) => props.theme.colors.port.grey};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 20px;
  }

  position: relative;
  z-index: 10;
  overflow-y: visible;
  overflow-x: clip;
  &::after {
    content: url(${PORT_BLUE_IMAGE_URL});
    display: none;

    position: absolute;
    z-index: 1;
    top: -169px;
    inset-inline-end: -200px;
    transform: rotate(270deg);
    ${(props) => props.theme.breakpoints.down("md")} {
      content: "";
      transform: rotate(180deg);
      background-image: url(${PORT_BLUE_IMAGE_URL});
      background-size: contain; /* or cover */
      background-repeat: no-repeat;
      width: 126px;
      height: 128px;
      top: unset;
      bottom: -61px;
    }
  }
`

const StyledItems = styled.ul`
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 1040px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-content: center;
  }
`

const StyledItemWrapper = styled.li`
  list-style: none;
  width: 100%;
  display: flex;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1;
    max-width: 331px;
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`

const StyledItem = styled.div`
  border-radius: 20px;
  border: 1px solid transparent;
  background: ${(props) => props.theme.colors.white};
  padding: 16px;
  padding-bottom: 24px;
  width: 100%;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  color: inherit;
  height: 100%;
  transition: all 0.3s ease-in-out;
  &:hover {
    border-color: ${(props) => props.theme.colors.port.defaultTextColor};
  }
`

const StyledItemIcon = styled.div`
  width: 50px;
  height: 50px;
  margin-bottom: 8px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }
`

const StyledItemTitle = styled.h3`
  font-size: 20px;
  font-weight: 400;
  line-height: 150%; /* 30px */
  letter-spacing: 0.6px;
  text-transform: capitalize;
  margin: 0;
  margin-bottom: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    letter-spacing: unset;
  }
`
const StyledItemText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%; /* 24px */
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const StyledItemButton = styled(Button)`
  margin-top: 16px;
  border: 1px solid ${(props) => props.theme.colors.port.defaultTextColor};
  border-radius: 100px;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  padding: 8px 16px;
  height: 40px;
  text-transform: none;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${(props) => props.theme.colors.port.defaultTextColor};
    color: #fff;
  }
`

type PortStrengthsComponentProps = { data: any }

type PortStrengthItemData = {
  title?: string
  text?: string
  icon?: string
  link?: {
    name: string | null
    target: string | null
    type?: "EXTERNAL" | "INTERNAL"
    url: string
  }
}

const PortStrengthItem = ({ item }: { item: PortStrengthItemData }) => {
  const href = item.link?.url ? getNonUmbracoUrl(item.link.url) : null
  const content = (
    <>
      {item.icon && (
        <StyledItemIcon>
          <Image
            src={getUmbracoMediaUrl(item.icon)}
            width={60}
            height={60}
            alt={item.title || "icon-alt"}
          />
        </StyledItemIcon>
      )}
      {item.title && <StyledItemTitle>{item.title}</StyledItemTitle>}
      {item.text && <StyledItemText>{item.text}</StyledItemText>}
      {item.link?.name && (
        <StyledItemButton variant="outlined" tabIndex={-1} aria-hidden="true">
          {item.link.name}
        </StyledItemButton>
      )}
    </>
  )
  if (href) {
    return (
      <StyledItemWrapper>
        <Link
          href={href}
          target={item.link?.target || (item.link?.type === "EXTERNAL" ? "_blank" : "_self")}
          style={{ width: "100%", height: "100%" }}
        >
          <StyledItem>{content}</StyledItem>
        </Link>
      </StyledItemWrapper>
    )
  }

  return (
    <StyledItemWrapper>
      <StyledItem as="div">{content}</StyledItem>
    </StyledItemWrapper>
  )
}

const PortStrengthsComponent = ({ data }: PortStrengthsComponentProps) => {
  const id = extractProperty(data, "id") || "port-strengths-component"
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const itemsList = extractProperty(data, "itemsList")
  const items = itemsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
    text: extractProperty(item, "text"),
    icon: extractPropertyMediaItems(item, "icon")?.[0],
    link: extractProperty(item, "link")?.[0],
  }))

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && (!items || items.length === 0)) {
    return null
  }
  return (
    <StyledSection id={id}>
      <PortTitle centered normalText={blackTitle} highlightedText={blueTitle} />
      <StyledItems>
        {items?.map((item: PortStrengthItemData, index: number) => (
          <PortStrengthItem key={index} item={item} />
        ))}
      </StyledItems>
    </StyledSection>
  )
}
export default PortStrengthsComponent
